// =========================================================
// Vojjin COLOR Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Color {

    /**
     * Normalizes a hexadecimal color code by removing the leading '#' if present,
     * and expanding shorthand notation (3-digit or 4-digit) to full-length notation
     * (6-digit or 8-digit).
     *
     * Valid input lengths: 3, 4, 6, or 8 (excluding the leading '#').
     * Returns `null` for invalid input lengths.
     *
     * @param {string} hex - The hexadecimal color code to normalize.
     * @return {string|null} The normalized hexadecimal color code, or `null` if the input is invalid.
     */
    static #normalizeHex = (hex) => {
        hex = hex.replace(/^#/, '');
        if (![3, 4, 6, 8].includes(hex.length)) return null;

        // Expand short hex
        if (hex.length === 3 || hex.length === 4)
            hex = hex.split('').map(c => c + c).join('');

        return hex;
    }

    /**
     * Converts an RGB or RGBA color string to its hexadecimal representation.
     *
     * @param {string} rgb - A string representing an RGB or RGBA color in the format
     *                       `rgb(r, g, b)` or `rgba(r, g, b, a)`, where `r`, `g`, and `b`
     *                       are integers between 0 and 255, and `a` is a floating-point
     *                       number between 0 and 1 representing the alpha channel.
     * @returns {string|null} The hexadecimal color representation, including the alpha
     *                        channel if present, or `null` if the input is not a valid
     *                        RGB or RGBA color string.
     */
    static toHex = (rgb) => {
        const match = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
        if (!match) return null;

        const [_, r, g, b, a] = match.map(Number);

        const toHex = n => n.toString(16).padStart(2, '0');
        const alpha = a !== undefined && a < 1
            ? toHex(Math.round(a * 255))
            : '';

        return `#${toHex(r)}${toHex(g)}${toHex(b)}${alpha}`;
    }

    /**
     * Converts a hexadecimal color code to an RGBA string.
     *
     * This function accepts a hex color code string in either shorthand (e.g., #rgb or #rgba)
     * or full-length (e.g., #rrggbb or #rrggbbaa) format. It returns the corresponding RGBA
     * string representation, adding an alpha channel if necessary. If the input is invalid,
     * the function returns `null`.
     *
     * @param {string} hex - The hexadecimal color code to convert.
     *                       It can start with a "#" character, which will be stripped.
     *                       Supported formats: #rgb, #rgba, #rrggbb, #rrggbbaa.
     *
     * @returns {string|null} The RGBA string in the format `rgba(r, g, b, a)`, or `null`
     *                        if the input is not valid.
     */
    static toRgba = (hex) => {
        hex = hex.replace(/^#/, '');
        if (![3, 4, 6, 8].includes(hex.length)) return null;

        // Expand short hex
        if (hex.length === 3 || hex.length === 4)
            hex = hex.split('').map(c => c + c).join('');

        const bigint = parseInt(hex, 16);
        const r = (bigint >> 24) & 255 || (bigint >> 16) & 255;
        const g = (bigint >> 16) & 255;
        const b = (bigint >> 8) & 255;
        const a = hex.length === 8 ? ((bigint & 255) / 255).toFixed(2) : 1;

        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    /**
     * Checks if the given string is a valid hex color code.
     *
     * This function evaluates whether the input string represents a valid
     * hexadecimal color. It removes the leading '#' if present and ensures
     * that the string length corresponds to standard hex color lengths:
     * 3 characters (short RGB), 4 characters (short RGBA),
     * 6 characters (full RGB), or 8 characters (full RGBA).
     *
     * @param {string} color - The color string to validate.
     * @returns {boolean} True if the input is a valid hex color; otherwise, false.
     */
    static isHexColor = (color) => {
        color = color.replace(/^#/, '');
        return [3, 4, 6, 8].includes(color.length);
    }

    /**
     * Checks if the given string matches the RGB or RGBA color format.
     *
     * The function validates whether the input string adheres to the format
     * of an RGB or RGBA color. RGB format is represented as `rgb(r, g, b)`,
     * and RGBA format is represented as `rgba(r, g, b, a)`, where:
     * - `r`, `g`, `b` are integers between 0 and 255 representing red, green, and blue color components, respectively.
     * - `a` is an optional decimal value between 0 and 1 representing the alpha (opacity) level.
     *
     * @param {string} color - The color string to validate.
     * @returns {Array|null} Returns an array of matched components if the input matches the pattern, or null if it does not.
     */
    static isRgbColor = (color) => {
        return color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
    }

    /**
     * Extracts the red component from a given color in hexadecimal or RGB format.
     *
     * This method determines the red component of the provided color. If the input is
     * in RGB format, it converts it into a hexadecimal format before determining the red value.
     * The color is validated to ensure it is in a proper hexadecimal or RGB format.
     *
     * @param {string} hexColor - The color input in hexadecimal or RGB format.
     * @returns {number|null} The red component (0-255) of the color, or `null` if the input is invalid.
     */
    static getRed = (hexColor) => {
        if (!this.isHexColor(hexColor) && !this.isRgbColor(hexColor)) return null;
        if (!this.isHexColor(hexColor)) hexColor = this.toHex(hexColor);
        return parseInt(this.#normalizeHex(hexColor).slice(0, 2), 16);
    }

    /**
     * Extracts the green component from a given color in hexadecimal or RGB format.
     *
     * The function takes an input color, which can be specified in either hexadecimal
     * format (e.g., #RRGGBB) or RGB string format (e.g., rgb(r, g, b)). If the input
     * color is in RGB format, it converts it to hexadecimal before extracting the green
     * component. If the input color is not a valid hexadecimal or RGB color, the function
     * returns null.
     *
     * @param {string} hexColor - A string representing the color in hexadecimal (#RRGGBB)
     *                            or RGB (rgb(r, g, b)) format.
     * @returns {number|null} The green color component as an integer (0-255), or null if
     *                        the input is invalid.
     */
    static getGreen = (hexColor) => {
        if (!this.isHexColor(hexColor) && !this.isRgbColor(hexColor)) return null;
        if (!this.isHexColor(hexColor)) hexColor = this.toHex(hexColor);
        return parseInt(this.#normalizeHex(hexColor).slice(2, 4), 16);
    }

    /**
     * Extracts and returns the blue component of a given color.
     *
     * This function accepts a color in hex or RGB format, validates the input,
     * converts the color to hex format if necessary, and then computes the blue
     * component of the color. If the input is invalid, it returns null.
     *
     * @param {string} hexColor - A color represented as a hex string (e.g., "#RRGGBB")
     *                            or an RGB string (e.g., "rgb(r, g, b)").
     * @returns {number|null} The blue component of the color as an integer
     *                        ranging from 0 to 255, or null if the input is invalid.
     */
    static getBlue = (hexColor) => {
        if (!this.isHexColor(hexColor) && !this.isRgbColor(hexColor)) return null;
        if (!this.isHexColor(hexColor)) hexColor = this.toHex(hexColor);
        return parseInt(this.#normalizeHex(hexColor).slice(4, 6), 16);
    }

    /**
     * Extracts and returns the alpha channel value from a given color in hexadecimal or RGB format.
     *
     * This function accepts a color input and determines whether it is in hexadecimal or RGB format.
     * It converts the RGB color to hexadecimal format if necessary, normalizes the hexadecimal string,
     * and calculates the alpha value. The alpha value is returned as a decimal between 0 and 1. If the
     * input color is invalid or does not contain an alpha component, the function returns `null`.
     *
     * @param {string} hexColor - The color input in hexadecimal or RGB format.
     * @returns {?number} The alpha channel value as a decimal between 0 and 1, or `null` if the
     * input is invalid or lacks an alpha component.
     */
    static getAlpha = (hexColor) => {
        if (!this.isHexColor(hexColor) && !this.isRgbColor(hexColor)) return null;
        if (!this.isHexColor(hexColor)) hexColor = this.toHex(hexColor);
        let alpha = parseInt(this.#normalizeHex(hexColor).slice(6, 8), 16);
        if (!Number.isNaN(alpha)) return alpha / 255;
        return null;
    }

    /**
     * Adjusts the lightness of a given hexadecimal color by a specified percentage.
     *
     * @param {string} hex - The hexadecimal color code to be lightened.
     *                       The input should be a valid 3 or 6-character hex color string, with or without a leading '#'.
     * @param {number} [amountPercentage=0] - The percentage by which to lighten the color.
     *                                         The value must be a positive number and will be normalized to a fraction of 100.
     *                                         Defaults to 0 if not specified.
     * @returns {string} - A new hex color string representing the lightened version of the input color.
     *                     The resulting color retains the same format (3 or 6-character string) as the input.
     */
    static lighten = (hex, amountPercentage = 0) => {
        return this.#lightenOrDarken(hex, Math.abs(amountPercentage / 100));
    }

    /**
     * Adjusts the brightness of a given hexadecimal color by darkening it
     * by a specified percentage.
     *
     * @param {string} hex - The hexadecimal color code to be darkened.
     * @param {number} [amountPercentage=0] - The percentage by which to darken
     *   the color. A positive value darkens the color, and the default is 0
     *   (no darkening applied).
     * @returns {string} The resulting hexadecimal color code after darkening.
     */
    static darken = (hex, amountPercentage = 0) => {
        return this.#lightenOrDarken(hex, -Math.abs(amountPercentage / 100));
    }

    /**
     * Sets a gradient background on a DOM element.
     *
     * @param {Element|string} el - The DOM element or selector.
     * @param {Object} options - Gradient configuration.
     * @param {string[]} [options.colors] - Array of color values.
     * @param {number[]|string[]} [options.stops] - Array of stop percentages (0–100 or with "%").
     * @param {string} [options.type="linear"] - Type: "linear", "radial", "conic".
     * @param {number|string} [options.angle=180] - Angle for linear gradient.
     */
    static setGradientBackground = (el, {colors=[], stops = [], type = "linear", angle = 180}) => {
        let domElement = $$(el);
        if (!domElement.defined()) return console.warn("Element not found");

        // Validate arrays
        if (!Array.isArray(colors) || colors.length < 2) {
            console.error("At least two colors required for gradient.");
            return;
        }

        // Ensure stops match colors
        if (stops.length && stops.length !== colors.length) {
            console.warn("Stops count doesn’t match colors count. Stops will be ignored.");
            stops = [];
        }

        // Combine colors and stops
        const gradientStops = colors.map((color, i) =>
            stops[i] ? `${color} ${typeof stops[i] === "number" ? stops[i] + "%" : stops[i]}` : color
        );

        // Build gradient string
        let gradient = "";

        switch (type) {
            case "radial":
                gradient = `radial-gradient(${gradientStops.join(", ")})`;
                break;
            case "conic":
                gradient = `conic-gradient(from ${angle}deg, ${gradientStops.join(", ")})`;
                break;
            default:
                gradient = `linear-gradient(${angle}deg, ${gradientStops.join(", ")})`;
        }

        // Apply background
        domElement.css("background", gradient);
        return gradient; // Return the string for debugging if needed
    }


    static #lightenOrDarken(hex, amount = 0) {
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');

        const r = parseInt(hex.substr(0, 2), 16) / 255;
        const g = parseInt(hex.substr(2, 2), 16) / 255;
        const b = parseInt(hex.substr(4, 2), 16) / 255;

        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0; // grayscale
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }

        l += amount;
        l = Math.min(1, Math.max(0, l));

        // Convert back to RGB
        function hue2rgb(p, q, t) {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        }

        let r2, g2, b2;

        if (s === 0) {
            r2 = g2 = b2 = l; // gray
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r2 = hue2rgb(p, q, h + 1 / 3);
            g2 = hue2rgb(p, q, h);
            b2 = hue2rgb(p, q, h - 1 / 3);
        }

        return `#${Math.round(r2 * 255).toString(16).padStart(2, '0')}${Math.round(g2 * 255).toString(16).padStart(2, '0')}${Math.round(b2 * 255).toString(16).padStart(2, '0')}`;
    }


}

registerGlobal("Color", Color);

